import type { Zone } from "../../api/types";
import { Banner, Callout } from "../../components/Callout";
import { Button } from "../../components/ui";
import { t, tServer } from "../../i18n/i18n";
import { useOpenZone } from "./useZones";
import { showZoneWait, zoneWaitLabel } from "./zoneAttention";
import { zoneRefusalOf } from "./zoneRefusal";
import { zoneBusy, zoneSuperseded, zoneWait } from "./zoneState";

/** État durable d'une vue qui attend le terrain d'une zone libre (docs/14 §2.7, §5.3) : exports, analyse du
 *  terrain, modes d'action, DIREX, 3A, logistique, ordre et mise en ambiance. Le message (celui du serveur quand
 *  il a refusé, sinon l'état de la zone) et le bouton qui mène à l'action attendue sur la fiche de la zone : la
 *  carte de progression, « Réessayer », « Libérer de l'espace » ou « Supprimer la zone ». */

/** Zone libre du dossier ouvert dont le terrain n'est pas (ou plus) utilisable ; null pour un camp ou une zone
 *  prête (une extension en cours laisse la zone utilisable). */
export function usePendingZone(): Zone | null {
  const { zone } = useOpenZone();
  return zone && zone.status !== "ready" ? zone : null;
}

/** Message de l'état de la zone, quand le serveur n'en a pas donné. */
export function zoneWaitMessage(zone: Zone | null): { title: string; detail: string } {
  if (!zone || zoneBusy(zone))
    return { title: t("Terrain en préparation"), detail: t("Cette vue sera disponible quand le terrain sera prêt.") };
  if (zoneWait(zone) === "remove")
    return {
      title: t("Aucune terre exploitable sur ce terrain"),
      detail: t("Supprimez ce terrain, puis tracez un nouvel axe sur la terre ferme."),
    };
  if (zone.status === "cancelled" && zoneSuperseded(zone))
    return {
      title: t("Préparation interrompue : un autre terrain est passé en priorité."),
      detail: t("Reprenez-la dans la vue Manœuvre pour utiliser le terrain."),
    };
  if (zone.status === "cancelled")
    return { title: t("Préparation annulée."), detail: t("Relancez-la dans la vue Manœuvre pour utiliser le terrain.") };
  return {
    title: zone.error_message ? tServer(zone.error_message) : t("La préparation n'a pas abouti."),
    detail: t("Relancez-la dans la vue Manœuvre pour utiliser le terrain."),
  };
}

/** Ton de l'attente, le même partout (encadré, bandeau, fiche de zone, avis — R10) : `danger` pour une préparation
 *  en échec ou une zone sans terre exploitable ; `info` pour la file, la préparation (état attendu) et une
 *  préparation annulée par l'utilisateur (pastille neutre de la fiche). */
export function zoneWaitTone(zone: Zone | null, error?: unknown): "danger" | "info" {
  return zoneRefusalOf(error) === "ZONE_NO_LAND" || zone?.status === "failed" ? "danger" : "info";
}

function WaitButton({ zone, error }: { zone: Zone | null; error?: unknown }) {
  return (
    <Button size="sm" variant="secondary" onClick={() => showZoneWait(zone, error)} className="whitespace-nowrap">
      {zoneWaitLabel(zone, error)}
    </Button>
  );
}

/** Encadré (panneaux) : `message` — refus du serveur déjà traduit (`ApiError.message`) ; `error` — ce refus, pour
 *  reconnaître une zone sans terre exploitable (l'action attendue est alors sa suppression). */
export function ZoneWaitCallout({ message, error, className }: { message?: string; error?: unknown; className?: string }) {
  const { zone } = useOpenZone();
  const state = zoneWaitMessage(zone);
  return (
    <div data-zone-wait="" className={className}>
      <Callout tone={zoneWaitTone(zone, error)} title={message ?? state.title} action={<WaitButton zone={zone} error={error} />}>
        {message ? undefined : state.detail}
      </Callout>
    </div>
  );
}

/** Bandeau (vues plein cadre, sous l'en-tête) : `message` — texte du serveur (`Order.stale`, indisponibilité de la
 *  mise en ambiance), traduit ici ; une zone sans terre exploitable s'y reconnaît (action attendue : la supprimer). */
export function ZoneWaitBanner({ message }: { message?: string | null }) {
  const { zone } = useOpenZone();
  const state = zoneWaitMessage(zone);
  // Même forme que `ApiError.raw` : le message d'origine du serveur, avant traduction.
  const refusal = message ? { raw: message } : undefined;
  return (
    <Banner tone={zoneWaitTone(zone, refusal)} actions={<WaitButton zone={zone} error={refusal} />}>
      {message ? tServer(message) : t("{label} : {value}", { label: state.title, value: state.detail })}
    </Banner>
  );
}

/** État d'une vue plein cadre (modes d'action, DIREX, 3A) refusée faute de terrain prêt, à la place de l'erreur. */
export function ZoneWaitState({ message, error }: { message?: string; error?: unknown }) {
  return (
    <div className="mx-auto w-full max-w-xl px-6 py-10 max-sm:px-4">
      <ZoneWaitCallout message={message} error={error} />
    </div>
  );
}
